import React, { useMemo, useState } from 'react';

import { Money, formatMoney } from '@betterment/core';
import { Box, HStack, Spinner, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ReturnsText } from '@betterment/shared-components';
import { ParentSize } from '@visx/responsive';

import { DateOptionToggleGroup } from './DateOptionToggleGroup';
import { DirectTradingAccountSecurityPricePerformanceQuery } from './graphql/directTradingAccountSecurityPricePerformance.generated';
import { PerformanceChart } from './PerformanceChart';
import { PerformanceDisclosureDialog } from './PerformanceDisclosureDialog';
import {
  type PricePointType,
  emptyChartDataForDate,
  formatDateTime,
  formatTimeDisplay,
  intradayPricePointsForTimeRange,
} from './utils';

import { DateRangeOption } from '../../../../../graphql/schema';
import { PlaceholderPerformanceChart } from '../../../../../Shared/components-v2/placeholder-performance-chart/PlaceholderPerformanceChart';

const timePeriods: { label: string; value: DateRangeOption }[] = [
  { label: 'Today', value: DateRangeOption.Today },
  { label: '1W', value: DateRangeOption.PastWeek },
  { label: '1M', value: DateRangeOption.PastMonth },
  { label: '3M', value: DateRangeOption.PastThreeMonths },
  { label: '1Y', value: DateRangeOption.PastYear },
  { label: 'All', value: DateRangeOption.AllTime },
];

const timePeriodForValue = (value: DateRangeOption | undefined) => {
  return timePeriods.find(timePeriod => timePeriod.value === value);
};

const overlayStyles = css.raw({
  bg: 'background.surface',
  opacity: '0.4',
});

const centeredSpinnerStyles = css.raw({
  position: 'absolute',
  top: '50%',
  left: '50%',
  translate: '-50% -50%',
  zIndex: '1',
});

type FinancialSecurityPricePerformancePricePointType =
  DirectTradingAccountSecurityPricePerformanceQuery['account']['financialSecurity']['pricePerformance']['timeSeries'][number];

type MarketScheduleType = DirectTradingAccountSecurityPricePerformanceQuery['marketSchedule'];

type PerformanceSectionProps = {
  latestPriceAmount: Money;
  latestPriceCurrentAsOf: string;
  latestReturnsAmount: Money;
  latestReturnsPercentage: number;
  timeSeries: FinancialSecurityPricePerformancePricePointType[];
  marketSchedule: MarketScheduleType;
  dateRangeOption: DateRangeOption;
  onDateRangeChange: (option: DateRangeOption) => void;
  isLoading?: boolean;
};

export const PerformanceSection: React.FC<PerformanceSectionProps> = ({
  latestPriceAmount,
  latestPriceCurrentAsOf,
  latestReturnsAmount,
  latestReturnsPercentage,
  timeSeries,
  marketSchedule,
  dateRangeOption,
  onDateRangeChange,
  isLoading = false,
}) => {
  const [highlight, setHighlight] = useState<PricePointType | null>(null);

  const shouldShowDateToggle = timeSeries.length > 0;
  const isTodayDateRangeOption = dateRangeOption === 'TODAY';
  const isMarketScheduleOpen = marketSchedule.openAt !== null && marketSchedule.closeAt !== null;

  const displayLatestPriceAmount = highlight?.amount || latestPriceAmount;
  const displayLatestReturnsAmount = highlight?.returns.amount || latestReturnsAmount;
  const displayLatestReturnsPercentage = highlight?.returns.percentage || latestReturnsPercentage;
  const dateRangeLabel = timePeriodForValue(dateRangeOption)?.label;

  const emptyChartData = emptyChartDataForDate({ date: new Date() });

  const timeSeriesCollection = useMemo(
    () =>
      timeSeries.map((record: FinancialSecurityPricePerformancePricePointType) => {
        return {
          __typename: record.__typename,
          dateTime: new Date(record.dateTime),
          amount: Money(record.amount.cents),
          returns: {
            __typename: record.returns.__typename,
            amount: Money(record.returns.amount.cents),
            percentage: record.returns.percentage,
          },
        };
      }),
    [timeSeries]
  );

  const getChartData = (): PricePointType[] => {
    if (isTodayDateRangeOption) {
      if (!isMarketScheduleOpen) return [];

      return intradayPricePointsForTimeRange({
        startTime: new Date(marketSchedule.openAt!),
        endTime: new Date(marketSchedule.closeAt!),
        pricePoints: timeSeriesCollection,
      });
    }

    if (timeSeriesCollection.length > 0) return timeSeriesCollection;
    if (latestPriceAmount.cents === 0) return [];
    return emptyChartData;
  };

  const chartData = getChartData();

  const formattedTime = (dateTime: Date) => {
    if (isTodayDateRangeOption) {
      return formatTimeDisplay(dateTime);
    }
    return formatDateTime(dateTime);
  };

  return (
    <VStack css={{ gap: 'space.300' }}>
      <VStack>
        <Text color="text" variant="text100">
          {highlight ? formattedTime(highlight.dateTime) : 'Last price'}
        </Text>
        <Text variant="text400">{formatMoney(displayLatestPriceAmount, 'default')}</Text>
        <HStack css={{ gap: 'space.50', alignItems: 'center' }}>
          <ReturnsText
            variant="text100"
            negativeToken="text.critical"
            amount={displayLatestReturnsAmount}
            percent={displayLatestReturnsPercentage}
          />
          {!highlight && <Text variant="text100">{dateRangeLabel}</Text>}
        </HStack>
      </VStack>

      <Box>
        {isLoading && <Spinner css={centeredSpinnerStyles} />}
        <Box css={{ ...(isLoading && overlayStyles), width: '100%', height: '100%' }}>
          <ParentSize>
            {({ width }) =>
              chartData.length > 0 ? (
                <PerformanceChart
                  width={width}
                  height={200}
                  data={chartData}
                  dateRangeOption={dateRangeOption}
                  disabled={isLoading}
                  onHighlightChange={setHighlight}
                />
              ) : (
                <PlaceholderPerformanceChart width={width} height={200} variant="zero-state" />
              )
            }
          </ParentSize>
        </Box>
      </Box>

      {shouldShowDateToggle && (
        <HStack css={{ justifyContent: 'center' }}>
          <DateOptionToggleGroup
            value={dateRangeOption}
            onValueChange={(val: DateRangeOption) => val && onDateRangeChange(val)}
          >
            {timePeriods.map(({ label, value: optionValue }) => (
              <DateOptionToggleGroup.Item key={optionValue} value={optionValue}>
                {label}
              </DateOptionToggleGroup.Item>
            ))}
          </DateOptionToggleGroup>
        </HStack>
      )}

      <Text variant="text25" color="text.weak">
        Price as of {latestPriceCurrentAsOf}. See <PerformanceDisclosureDialog />
      </Text>
    </VStack>
  );
};
